import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Github, Building2 } from 'lucide-react'

function getSafeRedirectPath(redirectParam: string | null): string {
  if (!redirectParam) {
    return '/overview'
  }

  if (
    !redirectParam.startsWith('/') ||
    redirectParam.startsWith('//') ||
    redirectParam.startsWith('/login') ||
    redirectParam.startsWith('/auth/callback')
  ) {
    return '/overview'
  }

  return redirectParam
}

export default function LoginPage() {
  const { loginWithGitHub, loginWithGoogle, loginWithMicrosoft, loginWithSSO, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [organization, setOrganization] = useState('')
  const [showSSO, setShowSSO] = useState(false)
  const redirectPath = getSafeRedirectPath(searchParams.get('redirect'))

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectPath])

  const handleGitHubLogin = () => {
    setIsLoading(true)
    loginWithGitHub(redirectPath)
  }

  const handleSSOLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (!organization.trim()) return
    setIsLoading(true)
    loginWithSSO(organization.trim(), redirectPath)
  }

  return (
    <main className="mx-auto flex min-h-[calc(100vh-4.5rem)] w-full max-w-7xl items-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="grid w-full gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(22rem,28rem)] lg:items-center">
        <section className="max-w-2xl space-y-5">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary/80">
            Authenticated workspace
          </p>
          <h1 className="text-4xl font-semibold tracking-tight text-foreground sm:text-5xl">
            Sign in to run validations, inspect networks, and review results.
          </h1>
          <p className="text-base leading-7 text-muted-foreground sm:text-lg">
            EtherPilot keeps the public product entry separate from the operator workspace. Use GitHub or SSO to enter the authenticated app shell.
          </p>
        </section>

        <Card className="w-full shadow-sm">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              Welcome to EtherPilot
            </CardTitle>
            <CardDescription className="text-center">
              Sign in to manage your network configurations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!showSSO ? (
              <>
                <Button
                  className="w-full"
                  size="lg"
                  onClick={handleGitHubLogin}
                  disabled={isLoading}
                >
                  <Github className="mr-2 h-5 w-5" />
                  {isLoading ? 'Redirecting...' : 'Sign in with GitHub'}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  size="lg"
                  onClick={() => { setIsLoading(true); loginWithGoogle(redirectPath) }}
                  disabled={isLoading}
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  {isLoading ? 'Redirecting...' : 'Sign in with Google'}
                </Button>

                <Button
                  variant="outline"
                  className="w-full"
                  size="lg"
                  onClick={() => { setIsLoading(true); loginWithMicrosoft(redirectPath) }}
                  disabled={isLoading}
                >
                  <svg className="mr-2 h-5 w-5" viewBox="0 0 23 23">
                    <path fill="#f35325" d="M1 1h10v10H1z"/>
                    <path fill="#81bc06" d="M12 1h10v10H12z"/>
                    <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                    <path fill="#ffba08" d="M12 12h10v10H12z"/>
                  </svg>
                  {isLoading ? 'Redirecting...' : 'Sign in with Microsoft'}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">
                      Or
                    </span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full"
                  size="lg"
                  onClick={() => setShowSSO(true)}
                  disabled={isLoading}
                >
                  <Building2 className="mr-2 h-5 w-5" />
                  Sign in with SSO
                </Button>

                <div className="text-center text-sm text-muted-foreground">
                  By signing in, you agree to our{' '}
                  <a href="/agreement" className="underline hover:text-foreground">
                    User Agreement
                  </a>
                </div>
              </>
            ) : (
              <>
                <form onSubmit={handleSSOLogin} className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="organization" className="text-sm font-medium">
                      Organization Domain or ID
                    </label>
                    <Input
                      id="organization"
                      type="text"
                      placeholder="acme-corp"
                      value={organization}
                      onChange={(e) => setOrganization(e.target.value)}
                      disabled={isLoading}
                      autoFocus
                    />
                    <p className="text-xs text-muted-foreground">
                      Enter your organization's domain or WorkOS organization ID
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={isLoading || !organization.trim()}
                  >
                    <Building2 className="mr-2 h-5 w-5" />
                    {isLoading ? 'Redirecting...' : 'Continue with SSO'}
                  </Button>
                </form>

                <Button
                  variant="ghost"
                  className="w-full"
                  onClick={() => setShowSSO(false)}
                  disabled={isLoading}
                >
                  Back to login options
                </Button>

                <div className="text-center text-sm text-muted-foreground">
                  By signing in, you agree to our{' '}
                  <a href="/agreement" className="underline hover:text-foreground">
                    User Agreement
                  </a>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
